import { isUndefined } from 'lodash';
import { StepViewComponent, StepConfigurationComponent, ResponseValidationResult } from './types';
import { ZodSchema } from 'zod';

import {
  InteractionStepType,
  InteractionStepResponse,
  IndexedInteractionStepResponse,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared-types';
import { IMultiTurnThreadHistory } from '../../ThreadHistory.interface';

import { maybeEnsureResponseSelfConsistency } from '@scale/shared/chat/response';
import { Types } from '@scale/scaleui';
import { JsonConfigurationComponent } from '../configs';
import { ResponseTemplatePath } from '../../ResponseTemplateCreator/ResponseTemplatePath';

export abstract class StepDescriptor<T extends InteractionStepType = InteractionStepType> {
  abstract type: InteractionStepType;
  abstract title: string;
  abstract description: string;

  abstract validationSchema: ZodSchema;

  /**
   * View component for tasking.
   * */
  abstract ViewComponent: StepViewComponent;

  /**
   * Configuration component for this step.
   * */
  ConfigurationComponent: StepConfigurationComponent<T> = JsonConfigurationComponent;

  /**
   * View component for pinned view.
   * */
  PinnedViewComponent?: StepViewComponent;

  /**
   * Whether this step can be pinned.
   * */
  get isPinnable() {
    return !isUndefined(this.PinnedViewComponent);
  }

  isResponseValid(
    _response: IndexedInteractionStepResponse,
    _stepParams: StepParamsForTypeWithStage<T>,
    _history: IMultiTurnThreadHistory,
    _reviewLevel?: ReviewLevel,
  ): ResponseValidationResult {
    return { canSubmit: true, submitTooltip: '' };
  }

  /**
   * If this function returns true, then the skip context is loaded from loadSkipContext
   * and overwrite the null context.
   * */
  requiresSkipContext(_stepParams: StepParamsForType<T>): boolean {
    return false;
  }

  /**
   * This async function is used to determine the context that is stored when this step
   * is skipped. It can be used to preload context for the next time it is visible.
   * */
  async loadSkipContext(
    _stepParams: StepParamsForType<T>,
    _history: IMultiTurnThreadHistory,
  ): Promise<any> {
    return {};
  }

  /**
   * Execute this function as soon as the step is known to be required.
   * */
  async doPrefetch(
    _stepParams: StepParamsForType<T>,
    _history: IMultiTurnThreadHistory,
  ): Promise<void> {
    return;
  }

  // TODO: @Lajos We will have nullContext as well once we have the chat package
  // abstract getNullContext(): Record<string, unknown>;

  // ///////////////////////////
  // Task Configuration specific methods
  // /////////////////////////

  getConfigurationPreviewTags(stepParams: StepParamsForType<T>): Types.Tag[] {
    // Returns a list of tags that are shown in the collapsed view of the config component.
    const tags: Types.Tag[] = [];
    const { params, override_id, id } = stepParams;
    if (params.visibility_restricted) {
      tags.push({
        value: `Level Visibility: ${params.visibility_restricted.join(', ')}`,
        variant: 'danger',
        icon: 'user-times',
        tooltipText: `Visible to levels ${params.visibility_restricted.join(', ')}`,
      });
    }
    if ('readonly_review_levels' in params && params.readonly_review_levels?.length) {
      tags.push({
        value: `Readonly: ${params.readonly_review_levels.join(', ')}`,
        variant: 'warning',
        icon: 'eye',
        tooltipText: `Levels ${params.readonly_review_levels.join(', ')} cannot edit this step`,
      });
    }
    if (params.editable_during_consensus) {
      tags.push({
        value: 'Consensus Step',
        variant: 'primary',
        icon: 'pen',
        tooltipText: 'This step will be editable during consensus (L4) stages',
      });
    }
    if (override_id) {
      tags.push({
        value: `"${id}"`,
        variant: 'info',
        icon: 'info',
        tooltipText: 'This step has an overridden ID',
      });
    }
    return tags;
  }

  // /////////////////////////
  // Response Templating specific methods
  // /////////////////////////
  getResponseTemplatePaths(
    _stepParams: StepParamsForType<T>,
    _history: IMultiTurnThreadHistory,
  ): ResponseTemplatePath[] {
    return [];
  }

  /**
   * This function is used to generate an empty response for this step. This is used to
   * infer the InteractionStep response format (needed for templated benchmarks creation).
   * */
  generateEmptyResponse(_stepParams: StepParamsForType<T>, _history: IMultiTurnThreadHistory): any {
    return {
      type: this.type,
      context: {},
      output: '',
    };
  }

  /**
   * This function is used to programmatically ensure an InteractionStep response is consistent with
   * itself (ex: confirming that selectedIndex actually matches selectedId for ModelResponseSelectors).
   * */
  ensureResponseConsistency(
    _stepParams: StepParamsForType<T>,
    _history: IMultiTurnThreadHistory,
    response: InteractionStepResponse,
  ) {
    maybeEnsureResponseSelfConsistency(_stepParams, response);
  }
}

export default StepDescriptor;
